import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export const DATE_FORMAT = "MM/DD/YYYY";

export function toLocalTimeConverter(date?: string | null) {
  if (!date) return;

  return new Date(date).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
}

export const TIME_ZONE_MAPPER = {
  PST: "America/Los_Angeles",
  EST: "America/New_York",
  CST: "America/Chicago",
  MST: "America/Denver",
  AST: "America/Halifax",
  HST: "Pacific/Honolulu",
  EDT: "America/New_York",
  PDT: "America/Los_Angeles",
  CDT: "America/Chicago",
  ADT: "America/Halifax",
  MDT: "America/Denver",
  IST: "Asia/Kolkata",
  AKDT: "America/Juneau",
  AKST: "America/Juneau",
};

//Todo: remove export once clear with the changes for timezone

/**
 * @param minutes Duration in minutes
 * @returns Formatted string e.g. "15 Min", "1 Hr"
 */
export function formatDurationFromMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} Min`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hrs} Hr`;
  return `${hrs} Hr ${mins} Min`;
}

/**
 *
 * @param date Date in ISO format e.g. `1970-01-01T00:00:03.000Z`
 * @returns Local Time string e.g. `01/01/1970, 05:30:03`
 */
export function formatDateTime(
  date: string | Date | undefined | null,
  options?: {
    withSeconds?: boolean;
    with24Hour?: boolean;
  },
  timezone?: string
) {
  if (!date) return "-";

  const hh = options?.with24Hour ? "HH" : "hh";
  const time = options?.withSeconds ? `${hh}:mm:ss A` : `${hh}:mm A`;
  const fmt = `${DATE_FORMAT}, ${time}`;

  // const { currentProviderTimeZone, isDayLightSavingTime } =
  //   getCurrentProviderTimeZone();

  return (
    dayjs(date)
      // .add(isDayLightSavingTime ? 1 : 0, "hour")
      // .tz(timezone ?? currentProviderTimeZone)
      .tz(timezone)
      ?.format(fmt)
  );
}

export function formatDate(
  date: string | Date | undefined | null,
  timezone?: string,
  fmt?: string
) {
  if (!date) return;

  // const { currentProviderTimeZone, isDayLightSavingTime } =
  //   getCurrentProviderTimeZone();

  return (
    dayjs(date)
      // .add(isDayLightSavingTime ? 1 : 0, "hour")
      // .tz(timezone ?? currentProviderTimeZone)
      .tz(timezone ?? undefined)
      .format(fmt || DATE_FORMAT)
  );
}

export const getFormattedDateWithoutTmz = (
  date: string | Date | undefined | null
) => {
  return dayjs(date).format(DATE_FORMAT);
};

export function formatTime(
  date: string | Date | undefined | null,
  options?: {
    withSeconds?: boolean;
    timezone?: TimezoneEnum;
  }
) {
  const fmt = options?.withSeconds ? "hh:mm:ss A" : "hh:mm A";

  // const { currentProviderTimeZone, isDayLightSavingTime } =
  //   getCurrentProviderTimeZone();

  return (
    dayjs(date)
      // .add(isDayLightSavingTime ? 1 : 0, "hour")
      // .tz(options?.timezone ?? currentProviderTimeZone)
      .tz(options?.timezone)
      .format(fmt)
  );
}

export const TimezoneEnum = {
  AfricaAbidjan: "Africa/Abidjan",
  AfricaAccra: "Africa/Accra",
  AfricaAddisAbaba: "Africa/Addis_Ababa",
  AfricaAlgiers: "Africa/Algiers",
  AfricaAsmara: "Africa/Asmara",
  AfricaBamako: "Africa/Bamako",
  AfricaBangui: "Africa/Bangui",
  AfricaBanjul: "Africa/Banjul",
  AfricaBissau: "Africa/Bissau",
  AfricaBlantyre: "Africa/Blantyre",
  AfricaBrazzaville: "Africa/Brazzaville",
  AfricaBujumbura: "Africa/Bujumbura",
  AfricaCairo: "Africa/Cairo",
  AfricaCasablanca: "Africa/Casablanca",
  AfricaCeuta: "Africa/Ceuta",
  AfricaConakry: "Africa/Conakry",
  AfricaDakar: "Africa/Dakar",
  AfricaDarEsSalaam: "Africa/Dar_es_Salaam",
  AfricaDjibouti: "Africa/Djibouti",
  AfricaDouala: "Africa/Douala",
  AfricaElAaiun: "Africa/El_Aaiun",
  AfricaFreetown: "Africa/Freetown",
  AfricaGaborone: "Africa/Gaborone",
  AfricaHarare: "Africa/Harare",
  AfricaJohannesburg: "Africa/Johannesburg",
  AfricaJuba: "Africa/Juba",
  AfricaKampala: "Africa/Kampala",
  AfricaKhartoum: "Africa/Khartoum",
  AfricaKigali: "Africa/Kigali",
  AfricaKinshasa: "Africa/Kinshasa",
  AfricaLagos: "Africa/Lagos",
  AfricaLibreville: "Africa/Libreville",
  AfricaLome: "Africa/Lome",
  AfricaLuanda: "Africa/Luanda",
  AfricaLubumbashi: "Africa/Lubumbashi",
  AfricaLusaka: "Africa/Lusaka",
  AfricaMalabo: "Africa/Malabo",
  AfricaMaputo: "Africa/Maputo",
  AfricaMaseru: "Africa/Maseru",
  AfricaMbabane: "Africa/Mbabane",
  AfricaMogadishu: "Africa/Mogadishu",
  AfricaMonrovia: "Africa/Monrovia",
  AfricaNairobi: "Africa/Nairobi",
  AfricaNdjamena: "Africa/Ndjamena",
  AfricaNiamey: "Africa/Niamey",
  AfricaNouakchott: "Africa/Nouakchott",
  AfricaOuagadougou: "Africa/Ouagadougou",
  AfricaPortoNovo: "Africa/Porto-Novo",
  AfricaSaoTome: "Africa/Sao_Tome",
  AfricaTripoli: "Africa/Tripoli",
  AfricaTunis: "Africa/Tunis",
  AfricaWindhoek: "Africa/Windhoek",
  AmericaAdak: "America/Adak",
  AmericaAnchorage: "America/Anchorage",
  AmericaAnguilla: "America/Anguilla",
  AmericaAntigua: "America/Antigua",
  AmericaAraguaina: "America/Araguaina",
  AmericaArgentinaBuenosAires: "America/Argentina/Buenos_Aires",
  AmericaArgentinaCatamarca: "America/Argentina/Catamarca",
  AmericaArgentinaCordoba: "America/Argentina/Cordoba",
  AmericaArgentinaJujuy: "America/Argentina/Jujuy",
  AmericaArgentinaLaRioja: "America/Argentina/La_Rioja",
  AmericaArgentinaMendoza: "America/Argentina/Mendoza",
  AmericaArgentinaRioGallegos: "America/Argentina/Rio_Gallegos",
  AmericaArgentinaSalta: "America/Argentina/Salta",
  AmericaArgentinaSanJuan: "America/Argentina/San_Juan",
  AmericaArgentinaSanLuis: "America/Argentina/San_Luis",
  AmericaArgentinaTucuman: "America/Argentina/Tucuman",
  AmericaArgentinaUshuaia: "America/Argentina/Ushuaia",
  AmericaAruba: "America/Aruba",
  AmericaAsuncion: "America/Asuncion",
  AmericaAtikokan: "America/Atikokan",
  AmericaBahia: "America/Bahia",
  AmericaBahiaBanderas: "America/Bahia_Banderas",
  AmericaBarbados: "America/Barbados",
  AmericaBelem: "America/Belem",
  AmericaBelize: "America/Belize",
  AmericaBlancSablon: "America/Blanc-Sablon",
  AmericaBoaVista: "America/Boa_Vista",
  AmericaBogota: "America/Bogota",
  AmericaBoise: "America/Boise",
  AmericaCambridgeBay: "America/Cambridge_Bay",
  AmericaCampoGrande: "America/Campo_Grande",
  AmericaCancun: "America/Cancun",
  AmericaCaracas: "America/Caracas",
  AmericaCayenne: "America/Cayenne",
  AmericaCayman: "America/Cayman",
  AmericaChicago: "America/Chicago",
  AmericaChihuahua: "America/Chihuahua",
  AmericaCiudadJuarez: "America/Ciudad_Juarez",
  AmericaCostaRica: "America/Costa_Rica",
  AmericaCreston: "America/Creston",
  AmericaCuiaba: "America/Cuiaba",
  AmericaCuracao: "America/Curacao",
  AmericaDanmarkshavn: "America/Danmarkshavn",
  AmericaDawson: "America/Dawson",
  AmericaDawsonCreek: "America/Dawson_Creek",
  AmericaDenver: "America/Denver",
  AmericaDetroit: "America/Detroit",
  AmericaDominica: "America/Dominica",
  AmericaEdmonton: "America/Edmonton",
  AmericaEirunepe: "America/Eirunepe",
  AmericaElSalvador: "America/El_Salvador",
  AmericaFortNelson: "America/Fort_Nelson",
  AmericaFortaleza: "America/Fortaleza",
  AmericaGlaceBay: "America/Glace_Bay",
  AmericaGooseBay: "America/Goose_Bay",
  AmericaGrandTurk: "America/Grand_Turk",
  AmericaGrenada: "America/Grenada",
  AmericaGuadeloupe: "America/Guadeloupe",
  AmericaGuatemala: "America/Guatemala",
  AmericaGuayaquil: "America/Guayaquil",
  AmericaGuyana: "America/Guyana",
  AmericaHalifax: "America/Halifax",
  AmericaHavana: "America/Havana",
  AmericaHermosillo: "America/Hermosillo",
  AmericaIndianaIndianapolis: "America/Indiana/Indianapolis",
  AmericaIndianaKnox: "America/Indiana/Knox",
  AmericaIndianaMarengo: "America/Indiana/Marengo",
  AmericaIndianaPetersburg: "America/Indiana/Petersburg",
  AmericaIndianaTellCity: "America/Indiana/Tell_City",
  AmericaIndianaVevay: "America/Indiana/Vevay",
  AmericaIndianaVincennes: "America/Indiana/Vincennes",
  AmericaIndianaWinamac: "America/Indiana/Winamac",
  AmericaInuvik: "America/Inuvik",
  AmericaIqaluit: "America/Iqaluit",
  AmericaJamaica: "America/Jamaica",
  AmericaJuneau: "America/Juneau",
  AmericaKentuckyLouisville: "America/Kentucky/Louisville",
  AmericaKentuckyMonticello: "America/Kentucky/Monticello",
  AmericaKralendijk: "America/Kralendijk",
  AmericaLaPaz: "America/La_Paz",
  AmericaLima: "America/Lima",
  AmericaLosAngeles: "America/Los_Angeles",
  AmericaLowerPrinces: "America/Lower_Princes",
  AmericaMaceio: "America/Maceio",
  AmericaManagua: "America/Managua",
  AmericaManaus: "America/Manaus",
  AmericaMarigot: "America/Marigot",
  AmericaMartinique: "America/Martinique",
  AmericaMatamoros: "America/Matamoros",
  AmericaMazatlan: "America/Mazatlan",
  AmericaMenominee: "America/Menominee",
  AmericaMerida: "America/Merida",
  AmericaMetlakatla: "America/Metlakatla",
  AmericaMexicoCity: "America/Mexico_City",
  AmericaMiquelon: "America/Miquelon",
  AmericaMoncton: "America/Moncton",
  AmericaMonterrey: "America/Monterrey",
  AmericaMontevideo: "America/Montevideo",
  AmericaMontserrat: "America/Montserrat",
  AmericaNassau: "America/Nassau",
  AmericaNewYork: "America/New_York",
  AmericaNome: "America/Nome",
  AmericaNoronha: "America/Noronha",
  AmericaNorthDakotaBeulah: "America/North_Dakota/Beulah",
  AmericaNorthDakotaCenter: "America/North_Dakota/Center",
  AmericaNorthDakotaNewSalem: "America/North_Dakota/New_Salem",
  AmericaNuuk: "America/Nuuk",
  AmericaOjinaga: "America/Ojinaga",
  AmericaPanama: "America/Panama",
  AmericaParamaribo: "America/Paramaribo",
  AmericaPhoenix: "America/Phoenix",
  AmericaPortAuPrince: "America/Port-au-Prince",
  AmericaPortOfSpain: "America/Port_of_Spain",
  AmericaPortoVelho: "America/Porto_Velho",
  AmericaPuertoRico: "America/Puerto_Rico",
  AmericaPuntaArenas: "America/Punta_Arenas",
  AmericaRankinInlet: "America/Rankin_Inlet",
  AmericaRecife: "America/Recife",
  AmericaRegina: "America/Regina",
  AmericaResolute: "America/Resolute",
  AmericaRioBranco: "America/Rio_Branco",
  AmericaSantarem: "America/Santarem",
  AmericaSantiago: "America/Santiago",
  AmericaSantoDomingo: "America/Santo_Domingo",
  AmericaSaoPaulo: "America/Sao_Paulo",
  AmericaScoresbysund: "America/Scoresbysund",
  AmericaSitka: "America/Sitka",
  AmericaStBarthelemy: "America/St_Barthelemy",
  AmericaStJohns: "America/St_Johns",
  AmericaStKitts: "America/St_Kitts",
  AmericaStLucia: "America/St_Lucia",
  AmericaStThomas: "America/St_Thomas",
  AmericaStVincent: "America/St_Vincent",
  AmericaSwiftCurrent: "America/Swift_Current",
  AmericaTegucigalpa: "America/Tegucigalpa",
  AmericaThule: "America/Thule",
  AmericaTijuana: "America/Tijuana",
  AmericaToronto: "America/Toronto",
  AmericaTortola: "America/Tortola",
  AmericaVancouver: "America/Vancouver",
  AmericaWhitehorse: "America/Whitehorse",
  AmericaWinnipeg: "America/Winnipeg",
  AmericaYakutat: "America/Yakutat",
  AntarcticaCasey: "Antarctica/Casey",
  AntarcticaDavis: "Antarctica/Davis",
  AntarcticaDumontDUrville: "Antarctica/DumontDUrville",
  AntarcticaMacquarie: "Antarctica/Macquarie",
  AntarcticaMawson: "Antarctica/Mawson",
  AntarcticaMcMurdo: "Antarctica/McMurdo",
  AntarcticaPalmer: "Antarctica/Palmer",
  AntarcticaRothera: "Antarctica/Rothera",
  AntarcticaSyowa: "Antarctica/Syowa",
  AntarcticaTroll: "Antarctica/Troll",
  AntarcticaVostok: "Antarctica/Vostok",
  ArcticLongyearbyen: "Arctic/Longyearbyen",
  AsiaAden: "Asia/Aden",
  AsiaAlmaty: "Asia/Almaty",
  AsiaAmman: "Asia/Amman",
  AsiaAnadyr: "Asia/Anadyr",
  AsiaAqtau: "Asia/Aqtau",
  AsiaAqtobe: "Asia/Aqtobe",
  AsiaAshgabat: "Asia/Ashgabat",
  AsiaAtyrau: "Asia/Atyrau",
  AsiaBaghdad: "Asia/Baghdad",
  AsiaBahrain: "Asia/Bahrain",
  AsiaBaku: "Asia/Baku",
  AsiaBangkok: "Asia/Bangkok",
  AsiaBarnaul: "Asia/Barnaul",
  AsiaBeirut: "Asia/Beirut",
  AsiaBishkek: "Asia/Bishkek",
  AsiaBrunei: "Asia/Brunei",
  AsiaChita: "Asia/Chita",
  AsiaChoibalsan: "Asia/Choibalsan",
  AsiaColombo: "Asia/Colombo",
  AsiaDamascus: "Asia/Damascus",
  AsiaDhaka: "Asia/Dhaka",
  AsiaDili: "Asia/Dili",
  AsiaDubai: "Asia/Dubai",
  AsiaDushanbe: "Asia/Dushanbe",
  AsiaFamagusta: "Asia/Famagusta",
  AsiaGaza: "Asia/Gaza",
  AsiaHebron: "Asia/Hebron",
  AsiaHoChiMinh: "Asia/Ho_Chi_Minh",
  AsiaHongKong: "Asia/Hong_Kong",
  AsiaHovd: "Asia/Hovd",
  AsiaIrkutsk: "Asia/Irkutsk",
  AsiaJakarta: "Asia/Jakarta",
  AsiaJayapura: "Asia/Jayapura",
  AsiaJerusalem: "Asia/Jerusalem",
  AsiaKabul: "Asia/Kabul",
  AsiaKamchatka: "Asia/Kamchatka",
  AsiaKarachi: "Asia/Karachi",
  AsiaKathmandu: "Asia/Kathmandu",
  AsiaKhandyga: "Asia/Khandyga",
  AsiaKolkata: "Asia/Kolkata",
  AsiaKrasnoyarsk: "Asia/Krasnoyarsk",
  AsiaKualaLumpur: "Asia/Kuala_Lumpur",
  AsiaKuching: "Asia/Kuching",
  AsiaKuwait: "Asia/Kuwait",
  AsiaMacau: "Asia/Macau",
  AsiaMagadan: "Asia/Magadan",
  AsiaMakassar: "Asia/Makassar",
  AsiaManila: "Asia/Manila",
  AsiaMuscat: "Asia/Muscat",
  AsiaNicosia: "Asia/Nicosia",
  AsiaNovokuznetsk: "Asia/Novokuznetsk",
  AsiaNovosibirsk: "Asia/Novosibirsk",
  AsiaOmsk: "Asia/Omsk",
  AsiaOral: "Asia/Oral",
  AsiaPhnomPenh: "Asia/Phnom_Penh",
  AsiaPontianak: "Asia/Pontianak",
  AsiaPyongyang: "Asia/Pyongyang",
  AsiaQatar: "Asia/Qatar",
  AsiaQostanay: "Asia/Qostanay",
  AsiaQyzylorda: "Asia/Qyzylorda",
  AsiaRiyadh: "Asia/Riyadh",
  AsiaSakhalin: "Asia/Sakhalin",
  AsiaSamarkand: "Asia/Samarkand",
  AsiaSeoul: "Asia/Seoul",
  AsiaShanghai: "Asia/Shanghai",
  AsiaSingapore: "Asia/Singapore",
  AsiaSrednekolymsk: "Asia/Srednekolymsk",
  AsiaTaipei: "Asia/Taipei",
  AsiaTashkent: "Asia/Tashkent",
  AsiaTbilisi: "Asia/Tbilisi",
  AsiaTehran: "Asia/Tehran",
  AsiaThimphu: "Asia/Thimphu",
  AsiaTokyo: "Asia/Tokyo",
  AsiaTomsk: "Asia/Tomsk",
  AsiaUlaanbaatar: "Asia/Ulaanbaatar",
  AsiaUrumqi: "Asia/Urumqi",
  AsiaUstNera: "Asia/Ust-Nera",
  AsiaVientiane: "Asia/Vientiane",
  AsiaVladivostok: "Asia/Vladivostok",
  AsiaYakutsk: "Asia/Yakutsk",
  AsiaYangon: "Asia/Yangon",
  AsiaYekaterinburg: "Asia/Yekaterinburg",
  AsiaYerevan: "Asia/Yerevan",
  AtlanticAzores: "Atlantic/Azores",
  AtlanticBermuda: "Atlantic/Bermuda",
  AtlanticCanary: "Atlantic/Canary",
  AtlanticCapeVerde: "Atlantic/Cape_Verde",
  AtlanticFaroe: "Atlantic/Faroe",
  AtlanticMadeira: "Atlantic/Madeira",
  AtlanticReykjavik: "Atlantic/Reykjavik",
  AtlanticSouthGeorgia: "Atlantic/South_Georgia",
  AtlanticStHelena: "Atlantic/St_Helena",
  AtlanticStanley: "Atlantic/Stanley",
  AustraliaAdelaide: "Australia/Adelaide",
  AustraliaBrisbane: "Australia/Brisbane",
  AustraliaBrokenHill: "Australia/Broken_Hill",
  AustraliaDarwin: "Australia/Darwin",
  AustraliaEucla: "Australia/Eucla",
  AustraliaHobart: "Australia/Hobart",
  AustraliaLindeman: "Australia/Lindeman",
  AustraliaLordHowe: "Australia/Lord_Howe",
  AustraliaMelbourne: "Australia/Melbourne",
  AustraliaPerth: "Australia/Perth",
  AustraliaSydney: "Australia/Sydney",
  CanadaAtlantic: "Canada/Atlantic",
  CanadaCentral: "Canada/Central",
  CanadaEastern: "Canada/Eastern",
  CanadaMountain: "Canada/Mountain",
  CanadaNewfoundland: "Canada/Newfoundland",
  CanadaPacific: "Canada/Pacific",
  EuropeAmsterdam: "Europe/Amsterdam",
  EuropeAndorra: "Europe/Andorra",
  EuropeAstrakhan: "Europe/Astrakhan",
  EuropeAthens: "Europe/Athens",
  EuropeBelgrade: "Europe/Belgrade",
  EuropeBerlin: "Europe/Berlin",
  EuropeBratislava: "Europe/Bratislava",
  EuropeBrussels: "Europe/Brussels",
  EuropeBucharest: "Europe/Bucharest",
  EuropeBudapest: "Europe/Budapest",
  EuropeBusingen: "Europe/Busingen",
  EuropeChisinau: "Europe/Chisinau",
  EuropeCopenhagen: "Europe/Copenhagen",
  EuropeDublin: "Europe/Dublin",
  EuropeGibraltar: "Europe/Gibraltar",
  EuropeGuernsey: "Europe/Guernsey",
  EuropeHelsinki: "Europe/Helsinki",
  EuropeIsleOfMan: "Europe/Isle_of_Man",
  EuropeIstanbul: "Europe/Istanbul",
  EuropeJersey: "Europe/Jersey",
  EuropeKaliningrad: "Europe/Kaliningrad",
  EuropeKirov: "Europe/Kirov",
  EuropeKyiv: "Europe/Kyiv",
  EuropeLisbon: "Europe/Lisbon",
  EuropeLjubljana: "Europe/Ljubljana",
  EuropeLondon: "Europe/London",
  EuropeLuxembourg: "Europe/Luxembourg",
  EuropeMadrid: "Europe/Madrid",
  EuropeMalta: "Europe/Malta",
  EuropeMariehamn: "Europe/Mariehamn",
  EuropeMinsk: "Europe/Minsk",
  EuropeMonaco: "Europe/Monaco",
  EuropeMoscow: "Europe/Moscow",
  EuropeOslo: "Europe/Oslo",
  EuropeParis: "Europe/Paris",
  EuropePodgorica: "Europe/Podgorica",
  EuropePrague: "Europe/Prague",
  EuropeRiga: "Europe/Riga",
  EuropeRome: "Europe/Rome",
  EuropeSamara: "Europe/Samara",
  EuropeSanMarino: "Europe/San_Marino",
  EuropeSarajevo: "Europe/Sarajevo",
  EuropeSaratov: "Europe/Saratov",
  EuropeSimferopol: "Europe/Simferopol",
  EuropeSkopje: "Europe/Skopje",
  EuropeSofia: "Europe/Sofia",
  EuropeStockholm: "Europe/Stockholm",
  EuropeTallinn: "Europe/Tallinn",
  EuropeTirane: "Europe/Tirane",
  EuropeUlyanovsk: "Europe/Ulyanovsk",
  EuropeVaduz: "Europe/Vaduz",
  EuropeVatican: "Europe/Vatican",
  EuropeVienna: "Europe/Vienna",
  EuropeVilnius: "Europe/Vilnius",
  EuropeVolgograd: "Europe/Volgograd",
  EuropeWarsaw: "Europe/Warsaw",
  EuropeZagreb: "Europe/Zagreb",
  EuropeZurich: "Europe/Zurich",
  Gmt: "GMT",
  IndianAntananarivo: "Indian/Antananarivo",
  IndianChagos: "Indian/Chagos",
  IndianChristmas: "Indian/Christmas",
  IndianCocos: "Indian/Cocos",
  IndianComoro: "Indian/Comoro",
  IndianKerguelen: "Indian/Kerguelen",
  IndianMahe: "Indian/Mahe",
  IndianMaldives: "Indian/Maldives",
  IndianMauritius: "Indian/Mauritius",
  IndianMayotte: "Indian/Mayotte",
  IndianReunion: "Indian/Reunion",
  PacificApia: "Pacific/Apia",
  PacificAuckland: "Pacific/Auckland",
  PacificBougainville: "Pacific/Bougainville",
  PacificChatham: "Pacific/Chatham",
  PacificChuuk: "Pacific/Chuuk",
  PacificEaster: "Pacific/Easter",
  PacificEfate: "Pacific/Efate",
  PacificFakaofo: "Pacific/Fakaofo",
  PacificFiji: "Pacific/Fiji",
  PacificFunafuti: "Pacific/Funafuti",
  PacificGalapagos: "Pacific/Galapagos",
  PacificGambier: "Pacific/Gambier",
  PacificGuadalcanal: "Pacific/Guadalcanal",
  PacificGuam: "Pacific/Guam",
  PacificHonolulu: "Pacific/Honolulu",
  PacificKanton: "Pacific/Kanton",
  PacificKiritimati: "Pacific/Kiritimati",
  PacificKosrae: "Pacific/Kosrae",
  PacificKwajalein: "Pacific/Kwajalein",
  PacificMajuro: "Pacific/Majuro",
  PacificMarquesas: "Pacific/Marquesas",
  PacificMidway: "Pacific/Midway",
  PacificNauru: "Pacific/Nauru",
  PacificNiue: "Pacific/Niue",
  PacificNorfolk: "Pacific/Norfolk",
  PacificNoumea: "Pacific/Noumea",
  PacificPagoPago: "Pacific/Pago_Pago",
  PacificPalau: "Pacific/Palau",
  PacificPitcairn: "Pacific/Pitcairn",
  PacificPohnpei: "Pacific/Pohnpei",
  PacificPortMoresby: "Pacific/Port_Moresby",
  PacificRarotonga: "Pacific/Rarotonga",
  PacificSaipan: "Pacific/Saipan",
  PacificTahiti: "Pacific/Tahiti",
  PacificTarawa: "Pacific/Tarawa",
  PacificTongatapu: "Pacific/Tongatapu",
  PacificWake: "Pacific/Wake",
  PacificWallis: "Pacific/Wallis",
  UsAlaska: "US/Alaska",
  UsArizona: "US/Arizona",
  UsCentral: "US/Central",
  UsEastern: "US/Eastern",
  UsHawaii: "US/Hawaii",
  UsMountain: "US/Mountain",
  UsPacific: "US/Pacific",
  Utc: "UTC",
} as const;

export type TimezoneEnum = (typeof TimezoneEnum)[keyof typeof TimezoneEnum];

export const getStartEndDateOfSameDay = (date: any) => {
  if (!date || !date.isValid || !date.isValid()) {
    return { startTime: null, endTime: null };
  }

  // Ensure the date is in dayjs format
  const dayjsDate = dayjs(date);

  // Set startTime (current time + 1 minute in UTC)
  const startTime = dayjsDate.startOf("day").utc().toISOString();

  // Set endTime (end of the day in UTC)
  const endTime = dayjsDate.endOf("day").utc().toISOString();

  return { startTime, endTime };
};

/**
 * To reduce the code snippet for  handling null value of date
 *
 * @see must use with Data grid Rendercell
 */
export const NonNullableDate = (v?: Date) => {
  return v ? formatDate(v) : "-";
};

export const isToday = (date?: Dayjs | null) =>
  date ? date?.isSame(dayjs(), "day") : false;

export function getOfficialTimes(date = dayjs()) {
  const startTime = dayjs(date).hour(9).minute(0).second(0).millisecond(0);

  const endTime = dayjs(date).hour(18).minute(0).second(0).millisecond(0);

  return { startTime, endTime };
}

export const getDateWithOffsetByMinusHr = (
  date: Date,
  offset: number
): Date => {
  return new Date(date.getTime() - offset * 60 * 60 * 1000);
};

// Subtracted calendar years only, so an age read one year high until the
// birthday passed. Callers use getAge in utils, which adjusts for month and day.
// export function getOnlyYear(birthDate: string | Date): string {
//   const today = new Date();
//   const birth = new Date(birthDate);
//   let years = today.getFullYear() - birth.getFullYear();
//
//   return `${years}`;
// }

export function isSameDay(d1: Date, d2: Date): boolean {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

export function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMins / 60);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHrs < 24) return `${diffHrs} hr ago`;

  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/** True for a picker value that was typed but is not a real date, e.g. "02" or "02/31/2025". */
export const isInvalidDate = (value: unknown): boolean =>
  dayjs.isDayjs(value) && !value.isValid();
